import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { usePerformanceStatsFiltersStorage } from '../../../../components/stats/filters/useStatsFilterStorage'
import {
  useProgrammaticCampaignPerformanceStatsQuery,
  useProgrammaticCampaignTimeseriesQuery,
} from '../../../../__generated__/graphql'
import { CampaignProgrammaticRoute } from '../../../routeParams'
import { FormattedProgrammaticCampaignPerformanceStats, StatsEntity } from '../../../../interfaces/stats'
import SectionTitle from '../../../../components/form/SectionTitle'
import { Section } from '../../../../components/layout/Section'
import { StatsFilters } from '../../../../components/stats/filters/StatsFilters'
import ProgrammaticCampaignPerformanceStatsTable from './ProgrammaticCampaignPerformanceStatsTable'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { flatResellersWebsites } from '../../../../utils/hooks/performanceStats/performanceStatsUtils'
import { useTimeseries } from '../../../../utils/hooks/performanceStats/useTimeseries'
import PerformanceStatsGraph from '../../../../components/stats/timeseries/PerformanceStatsGraph'

const Stats: React.FC = () => {
  const t = useAppTranslations()
  const { campaignProgrammaticId } = useParams<CampaignProgrammaticRoute>()
  const [performanceStatsDatesFilters, setPerformanceStatsDatesFilters] = usePerformanceStatsFiltersStorage()
  const statsVariables = {
    id: campaignProgrammaticId,
    startDate: performanceStatsDatesFilters.startDate,
    endDate: performanceStatsDatesFilters.endDate,
  }
  const {
    data: performanceStatsQueryData,
    loading: performanceStatsLoading,
    error: performanceStatsError,
  } = useProgrammaticCampaignPerformanceStatsQuery({
    variables: statsVariables,
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: timeSeriesQueryData,
    loading: timeseriesLoading,
    error: timeseriesError,
  } = useProgrammaticCampaignTimeseriesQuery({
    variables: {
      ...statsVariables,
      granularity: performanceStatsDatesFilters.granularity,
    },
    notifyOnNetworkStatusChange: true,
  })

  const {
    timeseriesData,
    chartItemsVisibility,
    toggleVisibility,
    chartOptions,
    setChartOptions,
    chartItems,
    dateFormat,
    timezone,
    setDateFormat,
  } = useTimeseries(timeSeriesQueryData?.campaignProgrammatic.timeseries, {
    filters: performanceStatsDatesFilters,
    entity: StatsEntity.ProgrammaticCampaign,
  })

  const formattedPerformanceStats: FormattedProgrammaticCampaignPerformanceStats | null = useMemo(() => {
    const performanceStats = performanceStatsQueryData?.campaignProgrammatic.performanceStats
    if (!performanceStats) return null
    const websites = flatResellersWebsites(performanceStats.publisherResellers)

    return {
      ...performanceStats,
      resellers: performanceStats.publisherResellers,
      websites,
      timeseries: timeseriesData?.timeseries ?? [],
    }
  }, [performanceStatsQueryData, timeseriesData])

  return (
    <Section sx={{ mb: 0, mt: 2 }}>
      <SectionTitle title={t('stats.title')} />
      <StatsFilters
        filters={performanceStatsDatesFilters}
        onChange={setPerformanceStatsDatesFilters}
        setChartDateFormat={setDateFormat}
        chartOptions={chartOptions}
        setChartOptions={setChartOptions}
      />
      <PerformanceStatsGraph
        timeseriesData={timeseriesData}
        chartItems={chartItems}
        chartItemsVisibility={chartItemsVisibility}
        toggleVisibility={toggleVisibility}
        isPublisherStats={false}
        dateFormat={dateFormat}
        timezone={timezone}
        chartOptions={chartOptions}
        loading={timeseriesLoading}
        error={timeseriesError}
      />
      <ProgrammaticCampaignPerformanceStatsTable
        performanceStats={formattedPerformanceStats}
        dateFormat={dateFormat}
        timezone={timezone}
        error={performanceStatsError}
        loading={performanceStatsLoading || timeseriesLoading}
      />
    </Section>
  )
}

export default Stats
